/**
 * RecordFieldGroupsSection — repeatable field groups on a Household or Family
 * Member record (TRUS-402).
 *
 * Repeatable groups ("Grants Applied For", "Survivor Needs" — a block you add a
 * row at a time) only ever rendered on the member profile. Ticking "Household"
 * on a field inside one saved successfully, persisted, and showed nothing,
 * because a group child's placement is governed by the GROUP's record types, not
 * by the child field's own `applies_to_record_types`. This is the missing
 * renderer for the other two records.
 *
 * It deliberately does NOT try to push group children into the flat field list.
 * Both flat field endpoints exclude them on purpose: a mandatory child rendered
 * as an ordinary field becomes a required input nobody can satisfy, and
 * client-side validation then blocks the save — that is what stopped member
 * creation at LCC for three weeks (TRUS-302). Groups render as groups, here.
 *
 * The field-drawing itself is the same RepeatableGroupRenderer the member
 * profile uses, so a group looks and behaves identically wherever it is placed.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RepeatableGroupRenderer } from '@/components/forms/RepeatableGroupRenderer';
import { fieldGroupsAPI, type FieldGroup } from '@/services/api/fieldGroups';

export type RecordScope = 'household' | 'family_member';

export interface RecordFieldGroupsSectionProps {
  recordType: RecordScope;
  /** Household id, for recordType='household'. */
  householdId?: number;
  /** Owning member id + family member id, for recordType='family_member'. */
  memberId?: number;
  familyMemberId?: number;
  /** Hide the outer heading when the parent already provides one. */
  hideHeading?: boolean;
}

export default function RecordFieldGroupsSection({
  recordType,
  householdId,
  memberId,
  familyMemberId,
  hideHeading,
}: RecordFieldGroupsSectionProps) {
  const [groups, setGroups] = useState<FieldGroup[]>([]);
  const [instances, setInstances] = useState<Record<string, Record<string, any>[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ids = { householdId, memberId, familyMemberId };
  const ready =
    recordType === 'household' ? householdId != null : memberId != null && familyMemberId != null;

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    try {
      // Groups placed on THIS record type, plus whatever this record has stored.
      const [defs, stored] = await Promise.all([
        fieldGroupsAPI.list({ record_type: recordType }),
        fieldGroupsAPI.listRecordInstances(recordType, ids),
      ]);
      setGroups(defs || []);
      setInstances(stored || {});
      setError(null);
    } catch (e: any) {
      // A group section that fails to load must not take the record page with it.
      console.warn('RecordFieldGroupsSection: failed to load', e);
      setError(e?.response?.data?.detail || e?.message || 'Failed to load field groups');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordType, householdId, memberId, familyMemberId, ready]);

  useEffect(() => {
    load();
  }, [load]);

  if (!ready || loading) {
    return loading ? (
      <div className="p-4 text-center text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
      </div>
    ) : null;
  }

  if (error) {
    return (
      <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 rounded-md">
        <AlertCircle className="w-4 h-4 inline mr-2" />
        {error}
      </div>
    );
  }

  // No groups placed on this record type — render nothing rather than an empty shell.
  if (!groups.length) return null;

  return (
    <div className="space-y-4">
      {!hideHeading && (
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Additional Sections</h3>
      )}
      {groups.map((group) => (
        <RecordGroupCard
          key={group.id}
          group={group}
          initialInstances={instances[group.name] || []}
          onSave={async (rows) => {
            await fieldGroupsAPI.setRecordInstances(recordType, ids, group.name, rows);
            await load();
          }}
        />
      ))}
    </div>
  );
}

// ============================================================================

const RecordGroupCard: React.FC<{
  group: FieldGroup;
  initialInstances: Record<string, any>[];
  onSave: (instances: Record<string, any>[]) => Promise<void>;
}> = ({ group, initialInstances, onSave }) => {
  // One RHF context per group — RepeatableGroupRenderer drives useFieldArray
  // under the group's name, exactly as it does on the member profile.
  const form = useForm<Record<string, any>>({
    defaultValues: { [group.name]: initialInstances },
    shouldUnregister: false,
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // An empty group starts collapsed so a record isn't padded out with unfilled
  // sections; one with data starts open.
  const [collapsed, setCollapsed] = useState(initialInstances.length === 0);

  useEffect(() => {
    form.reset({ [group.name]: initialInstances });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialInstances]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const rows = (form.getValues()[group.name] || []) as Record<string, any>[];
      await onSave(rows);
      setSavedAt(Date.now());
    } catch (e: any) {
      setSaveError(e?.response?.data?.detail || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const count = (form.watch(group.name) || []).length;

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="flex items-center gap-2 text-left"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <CardTitle className="text-sm">{group.friendly_name}</CardTitle>
            {count > 0 && <Badge variant="secondary">{count}</Badge>}
          </button>
          {!collapsed && (
            <div className="flex items-center gap-2">
              {saveError && <span className="text-xs text-red-600">{saveError}</span>}
              {savedAt && !saveError && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Saved
                </span>
              )}
              <Button type="button" size="sm" variant="outline" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                <span className="ml-1">Save</span>
              </Button>
            </div>
          )}
        </div>
        {group.description && !collapsed && (
          <p className="text-xs text-muted-foreground mt-1">{group.description}</p>
        )}
      </CardHeader>
      {!collapsed && (
        <CardContent className="pt-0">
          <FormProvider {...form}>
            <RepeatableGroupRenderer
              field={
                {
                  // RepeatableGroupRenderer resolves the group by field_group_id and
                  // keys form state by the group's name.
                  field_group_id: group.id,
                  name: group.name,
                  friendly_name: group.friendly_name,
                  type: 'field_group',
                } as any
              }
              compactMode={count >= 2}
            />
          </FormProvider>
        </CardContent>
      )}
    </Card>
  );
};
